import { getFirebaseAuth } from '@/lib/auth/firebase';
import { type Environment, initializeApi, isApiInitialized } from '@repo/api';
import type { RequestConfig, RequestInterceptor, UserProfile } from '@repo/api';
import { camelCaseKeysDeep } from '@repo/utils';
import { getAnonymousId } from '@/lib/anon-id';
import { getSessionCookieUrl } from '@/lib/api-web';
// import * as Sentry from '@sentry/nextjs'; // Sentry disabled (intentionally).

const shouldUseFirebaseToken = process.env.NEXT_PUBLIC_USE_FIREBASE_TOKEN === 'true';

/**
 * Auth token bridge for non-React code (like API interceptors).
 * We keep this module server-safe; the token is synced from the Zustand store
 * by a client-side effect (see `AuthEffects`).
 */
let currentAuthToken: string | null = null;

export const setApiAuthToken = (token: string | null) => {
  currentAuthToken = token;
};

/**
 * Request interceptor to automatically add Bearer token.
 * Token source of truth is the Zustand store (synced via `setApiAuthToken`).
 */
const authTokenInterceptor: RequestInterceptor = async (config: RequestConfig) => {
  if (typeof window === 'undefined') return config;
  if (config.skipAuth) return config;

  if (currentAuthToken && !config.token) {
    return {
      ...config,
      token: currentAuthToken,
    };
  }

  return config;
};

// ---------------------------------------------------------------------------
// Logout handler (single-flighted)
// ---------------------------------------------------------------------------
let logoutHandler: () => void | Promise<void> = () => {};
let logoutInFlight: Promise<void> | null = null;

export const setLogoutHandler = (handler: () => void | Promise<void>) => {
  logoutHandler = handler;
};

export const triggerLogout = async (): Promise<void> => {
  if (logoutInFlight) return logoutInFlight;

  logoutInFlight = (async () => {
    try {
      await logoutHandler();
    } finally {
      logoutInFlight = null;
    }
  })();

  return logoutInFlight;
};

// ---------------------------------------------------------------------------
// Session refresh (single-flighted)
// On 401/403, attempt to re-issue the session cookie before logging out.
// ---------------------------------------------------------------------------
type RefreshedSession = { ok?: boolean; expiresIn?: number; profile?: UserProfile };

let sessionRefreshInFlight: Promise<boolean> | null = null;
let sessionGate: Promise<boolean> | null = null;
let expiredSessionChecked = false;
let sessionRefreshedHandler: (session: RefreshedSession) => void = () => {};
let anonAutocompleteRetryAfterLogout = false;
let clearSessionInFlight: Promise<void> | null = null;
let omitCredentialsAfterClearSession = false;

function safeHasFirebaseUser(): boolean {
  try {
    const auth = getFirebaseAuth();
    return Boolean(auth?.currentUser);
  } catch {
    // If Firebase isn't initialized yet, treat as "no user" for logged-out recovery logic.
    return false;
  }
}

type PersistedWebAuth = { isLoggedIn?: unknown; sessionExpiresAt?: unknown };

function getPersistedWebAuth(): PersistedWebAuth | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage?.getItem('global-store');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { state?: { webAuth?: PersistedWebAuth } } | null;
    return parsed?.state?.webAuth ?? null;
  } catch {
    return null;
  }
}

function getPersistedWebIsLoggedIn(): boolean | null {
  const isLoggedIn = getPersistedWebAuth()?.isLoggedIn;
  return typeof isLoggedIn === 'boolean' ? isLoggedIn : null;
}

function isPersistedSessionExpired(): boolean {
  const webAuth = getPersistedWebAuth();
  const expiresAt = webAuth?.sessionExpiresAt;
  return (
    webAuth?.isLoggedIn === true && typeof expiresAt === 'number' && expiresAt <= Date.now() / 1000
  );
}

async function clearSessionCookieBestEffort(): Promise<void> {
  if (typeof window === 'undefined') return;
  try {
    await fetch('/api/clear-session', { method: 'POST', credentials: 'include' });
  } catch {
    // best-effort
  } finally {
    // Once we've attempted to clear the session cookie, avoid sending cookies on subsequent
    // logged-out requests unless explicitly overridden per-request.
    omitCredentialsAfterClearSession = true;
  }
}

async function clearSessionCookieSingleFlight(): Promise<void> {
  if (clearSessionInFlight) return clearSessionInFlight;
  clearSessionInFlight = (async () => {
    try {
      await clearSessionCookieBestEffort();
    } finally {
      clearSessionInFlight = null;
    }
  })();
  return clearSessionInFlight;
}

/**
 * Logged-out safety (enabled only after we attempted `/api/clear-session`):
 * avoid sending cookies to Openigloo service subdomains to prevent stale/wrong-domain
 * HttpOnly cookies from causing repeated 401/403 loops.
 */
const omitCredentialsAfterClearSessionInterceptor: RequestInterceptor = async (
  config: RequestConfig,
) => {
  if (typeof window === 'undefined') return config;
  if (!omitCredentialsAfterClearSession) return config;
  if (config.skipAuth) return config;

  const hasFirebaseUser = safeHasFirebaseUser();
  const persistedIsLoggedIn = getPersistedWebIsLoggedIn();
  if (hasFirebaseUser || persistedIsLoggedIn === true) return config;

  // Only apply to Openigloo hosts.
  try {
    const u = new URL(config.url, window.location.origin);
    const isOpeniglooHost = u.hostname === 'openigloo.com' || u.hostname.endsWith('.openigloo.com');
    if (!isOpeniglooHost) return config;
  } catch {
    return config;
  }

  // Force omit credentials unless the request explicitly overrides it.
  if (config.credentials === undefined) {
    return { ...config, credentials: 'omit' };
  }
  return config;
};

// ---------------------------------------------------------------------------
// Session-id header injection
// Adds an `X-Session-Id` header (sourced from the existing analytics anon id
// in localStorage — same one used by AnalyticsEffects) to a small allow-list
// of unit endpoints. Applies to BOTH logged-in and logged-out users so the
// backend can correlate views/filters per browser regardless of auth state.
//
// Endpoints covered:
//   - GET  /unit/{slug}/track_view/
//   - GET  /unit/{slug}/track_image_view/
//   - POST /unit/map_filter/
//   - POST /unit/list_filter/   (any query string, e.g. ?page=1&page_size=18)
//   - GET  /unit/recommendations/ (any query string, e.g. ?page=1&pageSize=6)
//   - POST /unit/{slug}/page_active/
//   - POST /unit/{slug}/page_inactive/
//   - *    /ai-search/*  (AI search — identifies anon users for rate limiting)
// ---------------------------------------------------------------------------
function isSessionIdTrackedUrl(rawUrl: string): boolean {
  try {
    const base = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
    const u = new URL(rawUrl, base);
    const path = u.pathname;
    // AI search: x-session-id is "always" per the contract (rate limiting + query log).
    if (/^\/ai-search\//.test(path)) return true;
    // /unit/{slug}/track_view/   (slug must be a single non-empty path segment)
    if (/^\/unit\/[^/]+\/track_view\/?$/.test(path)) return true;
    // /unit/{slug}/track_image_view/  (listing-card image browse — V3 drop-off)
    if (/^\/unit\/[^/]+\/track_image_view\/?$/.test(path)) return true;
    if (/^\/unit\/[^/]+\/page_(active|inactive)\/?$/.test(path)) return true;
    if (/^\/unit\/map_filter\/?$/.test(path)) return true;
    if (/^\/unit\/list_filter\/?$/.test(path)) return true;
    if (/^\/unit\/recommendations\/?$/.test(path)) return true;
    // New chats: messenger records which browser started the chat (experiment attribution).
    if (/^\/chat\/?$/.test(path)) return true;
    return false;
  } catch {
    return false;
  }
}

const sessionIdHeaderInterceptor: RequestInterceptor = async (config: RequestConfig) => {
  if (typeof window === 'undefined') return config;
  if (!isSessionIdTrackedUrl(config.url)) return config;

  // Prefer the existing analytics anon id (single source of truth in localStorage
  // under `__anon_id`). getAnonymousId() will create one on first call and reuse
  // it on every subsequent visit from the same browser — exactly the behavior
  // we want for a per-browser session id, regardless of login state.
  const sessionId = getAnonymousId();
  if (!sessionId) return config;

  return {
    ...config,
    headers: {
      ...(config.headers || {}),
      'x-session-id': sessionId,
    },
  };
};

async function parseResponseDetailBestEffort(response: Response): Promise<string | null> {
  try {
    const cloned = response.clone();
    const data = await cloned.json();
    return typeof data?.detail === 'string' ? data.detail : null;
  } catch {
    return null;
  }
}

function isBuildingAutocompleteUrl(rawUrl: string): boolean {
  try {
    const u = new URL(rawUrl);
    // Match both `/building/autocomplete` and `/building/autocomplete/` regardless of hostname (prod/ngrok/etc).
    return /^\/building\/autocomplete\/?$/.test(u.pathname);
  } catch {
    return (
      rawUrl.includes('/building/autocomplete?') ||
      rawUrl.endsWith('/building/autocomplete') ||
      rawUrl.endsWith('/building/autocomplete/')
    );
  }
}

export const setSessionRefreshedHandler = (handler: (session: RefreshedSession) => void) => {
  sessionRefreshedHandler = handler;
};

async function requestSessionCookie(): Promise<boolean> {
  try {
    const auth = getFirebaseAuth();
    await auth.authStateReady();
    const currentUser = auth.currentUser;
    if (!currentUser) return false;
    const idToken = await currentUser.getIdToken(true);
    const res = await fetch(getSessionCookieUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      credentials: 'include',
    });
    if (!res.ok) return false;

    const session = camelCaseKeysDeep<RefreshedSession | null>(await res.json());
    if (!session?.ok) return false;
    sessionRefreshedHandler(session);
    return true;
  } catch {
    return false;
  }
}

export function refreshSession({ holdRequests = true }: { holdRequests?: boolean } = {}) {
  if (typeof window === 'undefined') return Promise.resolve(false);

  if (!sessionRefreshInFlight) {
    sessionRefreshInFlight = requestSessionCookie().finally(() => {
      sessionRefreshInFlight = null;
      sessionGate = null;
    });
  }
  if (holdRequests) sessionGate = sessionRefreshInFlight;
  return sessionRefreshInFlight;
}

export async function ensureSession(): Promise<boolean> {
  if (!isPersistedSessionExpired()) return true;
  if (await refreshSession()) return true;
  await triggerLogout();
  return false;
}

// ---------------------------------------------------------------------------
// Request gate — pauses outgoing requests while a session refresh is in flight
// ---------------------------------------------------------------------------
const sessionGateInterceptor: RequestInterceptor = async (config: RequestConfig) => {
  if (!expiredSessionChecked && typeof window !== 'undefined') {
    expiredSessionChecked = true;
    if (isPersistedSessionExpired()) void refreshSession();
  }
  if (sessionGate) {
    await sessionGate;
  }
  return config;
};

// ---------------------------------------------------------------------------
// API configuration
// ---------------------------------------------------------------------------
const API_CONFIG = {
  environment:
    process.env.NEXT_PUBLIC_APP_ENV === 'production'
      ? 'production'
      : ('development' as Environment),
  timeout: 30000,
  retryAttempts: 0,
  ...(shouldUseFirebaseToken
    ? {
        requestInterceptors: [
          sessionGateInterceptor,
          omitCredentialsAfterClearSessionInterceptor,
          authTokenInterceptor,
          sessionIdHeaderInterceptor,
        ],
      }
    : {
        requestInterceptors: [
          sessionGateInterceptor,
          omitCredentialsAfterClearSessionInterceptor,
          sessionIdHeaderInterceptor,
        ],
        defaultCredentials: 'include' as RequestCredentials,
      }),
  responseInterceptors: [
    async (response: Response, retry: () => Promise<Response>) => {
      // Special-case: building autocomplete can 403 when a stale session cookie exists,
      // even if the user is effectively logged out (no Firebase user to refresh with).
      // In that case, the global 401/403 handler will clear the cookie via logout.
      // After that happens, we want the *next* attempt to succeed anonymously without affecting
      // the normal token refresh mechanism.
      if (response.status === 403 && typeof window !== 'undefined') {
        try {
          const url = response.url || '';
          if (isBuildingAutocompleteUrl(url)) {
            const hasFirebaseUser = safeHasFirebaseUser();
            if (!hasFirebaseUser) {
              const detail = await parseResponseDetailBestEffort(response);
              if (detail?.toLowerCase().includes('invalid or expired session cookie')) {
                // Mark that the next autocomplete attempt should be retried anonymously *after*
                // the logout/clear-session flow runs.
                anonAutocompleteRetryAfterLogout = true;
              }
            }
          }
        } catch {
          // best-effort; fall through to normal 401/403 handling
        }
      }

      if (response.status === 401 || response.status === 403) {
        const hasFirebaseUser = typeof window !== 'undefined' ? safeHasFirebaseUser() : false;
        // Sentry.captureMessage(`403 Forbidden: ${response.url}`, {
        //   level: 'warning',
        //   extra: {
        //     url: response.url,
        //     status: response.status,
        //     hasFirebaseUser,
        //   },
        // });

        // Logged-out recovery (no Firebase user): clear session cookie once (single-flight),
        // then retry this request once. This prevents missing retries on refresh and prevents
        // spamming clear-session when many requests 401/403 concurrently.
        if (!hasFirebaseUser && typeof window !== 'undefined') {
          const persistedIsLoggedIn = getPersistedWebIsLoggedIn();
          if (persistedIsLoggedIn !== true) {
            await clearSessionCookieSingleFlight();
            const retryResponse = await retry();
            if (retryResponse.status === 401 || retryResponse.status === 403) {
              void triggerLogout();
            }
            return retryResponse;
          }
        }

        const refreshed = await refreshSession();
        if (refreshed) {
          const retryResponse = await retry();
          if (retryResponse.status === 401 || retryResponse.status === 403) {
            void triggerLogout();
          }
          return retryResponse;
        }
        void triggerLogout();

        // If the logout path ran due to stale session cookie on building autocomplete,
        // attempt one anonymous retry (no credentials) so the user still gets suggestions.
        if (anonAutocompleteRetryAfterLogout) {
          anonAutocompleteRetryAfterLogout = false;
          try {
            const url = response.url || '';
            if (isBuildingAutocompleteUrl(url)) {
              return await fetch(url, { credentials: 'omit' });
            }
          } catch {
            // best-effort; fall through to returning original response
          }
        }
      }
      return response;
    },
  ],
};

export function ensureApiInitialized(): void {
  if (!isApiInitialized()) {
    initializeApi(API_CONFIG);
  }
}

ensureApiInitialized();

export const API_INITIALIZED = true;
